import { useCallback, useMemo, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { OrderLineItem, OrderRow } from "@features/orders-features/types/types";
import { OrderLineItemDto, PaginatedFilterQuery } from "@lib/ShiOneClient";
import { ShipmentGroup } from "../../order-detail-panel/ShipmentMeta";
import { OrderDrawerShipments, buildShipmentCards } from "@features/orders-features/components/order-drawer/sections/OrderDrawerShipments";
import { StackedOrderProductDetailDrawer } from "@features/orders-features/components/order-drawer/StackedOrderProductDetailDrawer";
import { useDrawerStack } from "shared-ui";
import type { RoutedTabControls } from "shared-ui/src/hooks/routedOverlays";
import { isOrderLineItemViewArray } from "@features/orders-features/functions/mapToOrderRowHelpers";
import ProductTable from "./content/ProductTable";
import { StackedTrackingDrawer } from "@features/orders-features/components/order-drawer/StackedTrackingDrawer";
import { useProductAvailability } from "@features/orders-features/hooks/useProductAvailability";
import { useGetOrderLineSerials } from "@features/orders-features/hooks/useGetOrderLineSerials";
import { useGetOrderLineRelatedData } from "@features/orders-features/hooks/useGetOrderLineRelatedData";
import { useDebouncedSearch } from "shared-ui/src/utils/useDebouncedSearch";
import { useGetWarehouseOrders } from "@features/assets-feature/hooks/warehouse/useGetWarehouseOrders";
import {
  getCategoryLabel,
  getCategoryType,
} from "@features/orders-features/functions/category";
import { CategoryType, LineItem } from "@features/orders-features/types/drawer";
import { ShiOnePermission } from "../../../../../../../auth/authorizationTypes";
import { useClaims } from "../../../../../../../auth/ClaimsProvider";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { useOrderTelemetrySection } from "../orderTelemetrySection";
import { toProductRouteId } from "../orderRouteKey";
import {
  ORDER_DRAWER_DEFAULT_TAB,
  PRODUCT_DRAWER_DEFAULT_TAB,
  PRODUCT_DRAWER_SEGMENT,
  TRACKING_DRAWER_SEGMENT,
  type OrderDrawerRouteTab,
} from "../orderDrawerSegments";
import {
  buildTrackingDrawerProps,
  toTrackingRouteId,
} from "../trackingDrawerProps";

function parseGroupParts(item: OrderLineItem): {
  status: string;
  productType: string;
  shipId?: number;
} {
  const parts = (item.group ?? "")
    .replace(/[()]/g, "")
    .split(",")
    .map((s) => s.trim());

  const getToken = (index: number) => {
    if (index < 0 || index >= parts.length) {
      return "";
    }
    const value = parts[index] ?? "";
    return value.trim();
  };

  const productType = (getToken(1) || "OTHER").toUpperCase();

  const getStatus = () => {
    const status = getToken(0);
    const lineStatus = (item.lineStatus ?? "").toLowerCase();

    if (status.toLowerCase() !== "notapplicable") {
      if (status.startsWith("Ship") && lineStatus.includes("deliver")) {
        return lineStatus;
      }
      return status;
    }

    const statusByProductType: Record<string, string> = {
      SOFTWARE: "Email Delivery",
      SERVICE: "Service",
      FEES: "Fees",
    };

    if (lineStatus === "invoiced") {
      const productStatus = statusByProductType[productType];
      if (productStatus) {
        return productStatus;
      }
    }

    if (
      lineStatus === "orderplaced" &&
      (productType === "SOFTWARE" ||
        productType === "SERVICE" ||
        productType === "FEES")
    ) {
      return "Processing";
    }

    if (lineStatus === "open order") {
      if (item.status?.toLowerCase() === "open order") {
        return "Processing";
      }
      return item.status || status;
    }

    return item.lineStatus || item.status || status;
  };

  const getShipId = () => {
    const value = getToken(2);
    if (value === "") {
      return undefined;
    }
    const shipId = Number(value);
    return Number.isFinite(shipId) ? shipId : undefined;
  };

  return {
    status: getStatus(),
    productType,
    shipId: getShipId(),
  };
}

export function OrderDrawerDetails({
  order,
  disableGutters = false,
  tabControls,
}: Readonly<{
  order: OrderRow;
  disableGutters?: boolean;
  tabControls?: RoutedTabControls<OrderDrawerRouteTab>;
}>) {
  const [searchTextValue, setSearchTextValue] = useState<string>("");
  const debouncedSearch = useDebouncedSearch(searchTextValue);
  const [selectedCategories, setSelectedCategories] = useState<
    Set<Exclude<CategoryType, "all">>
  >(new Set());
  const [localTab, setLocalTab] = useState<OrderDrawerRouteTab>(
    ORDER_DRAWER_DEFAULT_TAB,
  );
  const tab = tabControls?.activeTab ?? localTab;
  const setTab = tabControls?.setTab ?? setLocalTab;
  const { pushDrawer } = useDrawerStack();
  const { hasPermission, getClaimValue } = useClaims();
  const telemetrySection = useOrderTelemetrySection();
  const rawOrgUnitIdCurrent = getClaimValue("orgUnitIdCurrent");
  const currentOrgUnitId =
    rawOrgUnitIdCurrent != null && Number.isFinite(Number(rawOrgUnitIdCurrent))
      ? Number(rawOrgUnitIdCurrent)
      : null;

  const { data: serials, isLoading: isLoadingSerials } = useGetOrderLineSerials(
    order.biOrderId ?? undefined,
  );
  const { data: relatedData, isLoading: isLoadingRelatedData } =
    useGetOrderLineRelatedData(order.biOrderId ?? undefined);

  const warehouseOrdersQuery = useMemo(
    () =>
      new PaginatedFilterQuery({
        page: 1,
        pageSize: 25,
        sortByColumn: "orderDate",
        isDescending: true,
        searchString: order.salesOrder ?? undefined,
      }),
    [order.salesOrder],
  );
  const { data: warehouseOrders, isLoading: isLoadingWarehouseOrders } =
    useGetWarehouseOrders(warehouseOrdersQuery);

  const lines = useMemo<LineItem[]>(() => {
    if (
      !isOrderLineItemViewArray(order.allLineItems) ||
      order.allLineItems.length === 0
    ) {
      return [];
    }
    return order.allLineItems.map((item) => {
      const { status, productType, shipId } = parseGroupParts(item);
      const grp = (item.group ?? "").replace(/[\s,]+/g, "|");
      return {
        id:
          item.lineItemId ||
          `${item.productDescriptor}-${item.productClass}-${item.lineNumber}-${grp}`,
        item,
        category: getCategoryLabel(productType),
        categoryType: getCategoryType(productType),
        status,
        shipId,
      };
    });
  }, [order.allLineItems]);

  const { isBackorder, effectiveLines, isLoadingProductAvailability } =
    useProductAvailability(
      order.biOrderId ?? order.webOrderId ?? undefined,
      order.orgUnitId,
      lines,
    );

  const availableFilters = useMemo(
    () => {
      const categories = new Map<Exclude<CategoryType, "all">, string>();
      effectiveLines.forEach((line) => {
        categories.set(
          line.categoryType,
          line.categoryType === "services" ? "Services" : line.category,
        );
      });

      return [
        { key: "all" as const, label: "All" },
        ...Array.from(categories, ([key, label]) => ({ key, label })),
      ];
    },
    [effectiveLines],
  );

  const filteredItems = useMemo(() => {
    const searchValue = debouncedSearch.toLowerCase();
    return effectiveLines.filter((p) => {
      // Apply category filter first
      if (
        selectedCategories.size > 0 &&
        !selectedCategories.has(p.categoryType)
      ) {
        return false;
      }
      if (searchValue.length === 0) {
        return true;
      }

      const like = (
        field: string | null | undefined,
        search: string,
      ): boolean => (field ?? "").toLowerCase().includes(search);

      // In-order: mfgPartNumber → shiPartNumber → productName → productDescription → serial # → asset tag → custom fields
      if (like(p.item.mfgPartNumber, searchValue)) return true;
      if (like(p.item.shiPartNumber, searchValue)) return true;
      if (like(p.item.productDescriptor, searchValue)) return true;
      if (like(p.item.productName, searchValue)) return true;
      if (like(p.item.productDescription, searchValue)) return true;
      if (serials) {
        const lineSerials = serials.filter(
          (s) => s.lineNumber === p.item.lineNumber,
        );
        if (
          lineSerials.some(
            (s) =>
              like(s.serialNumber, searchValue) ||
              like(s.assetTag, searchValue),
          )
        )
          return true;
      }
      if (relatedData) {
        const lineRelated = relatedData.filter(
          (r) => r.lineNumber === p.item.lineNumber,
        );
        if (
          lineRelated.some(
            (r) =>
              like(r.fieldName, searchValue) ||
              like(r.valueString, searchValue),
          )
        )
          return true;
      }
      return false;
    });
  }, [
    selectedCategories,
    effectiveLines,
    debouncedSearch,
    serials,
    relatedData,
  ]);

  const myWarehouseOrders = useMemo(() => {
    if (!order.salesOrder || !warehouseOrders?.items?.length) {
      return null;
    }
    return warehouseOrders.items.filter(
      (p) =>
        p.salesId === order.salesOrder &&
        p.orderType?.toLowerCase() === "config",
    );
  }, [warehouseOrders?.items, order.salesOrder]);

  const filteredShipmentGroups = useMemo<Record<string, OrderLineItemDto[]>>(() => {
    const dict = (order.orderLineProducts ?? {}) as Record<
      string,
      OrderLineItemDto[]
    >;

    // No active filters - return all non-empty groups as-is
    if (debouncedSearch.length === 0 && selectedCategories.size === 0) {
      return Object.fromEntries(
        Object.entries(dict).filter(([, items]) => items.length > 0),
      );
    }

    // Build identifiers for items that pass the current search/category filter
    const passedKeys = new Set<string>();
    for (const fi of filteredItems) {
      if (fi.item.lineItemId) passedKeys.add(fi.item.lineItemId);
      if (fi.item.lineNumber != null) passedKeys.add(`ln-${fi.item.lineNumber}`);
    }

    const result: Record<string, OrderLineItemDto[]> = {};
    for (const [key, items] of Object.entries(dict)) {
      const filtered = items.filter((item) => {
        if (item.lineItemId && passedKeys.has(item.lineItemId)) return true;
        return (
          item.lineNumber != null && passedKeys.has(`ln-${item.lineNumber}`)
        );
      });
      if (filtered.length > 0) result[key] = filtered;
    }
    return result;
  }, [order.orderLineProducts, filteredItems, debouncedSearch, selectedCategories]);


  const shipmentCount = useMemo(
    () => buildShipmentCards(filteredShipmentGroups).length,
    [filteredShipmentGroups],
  );

  const handleProductClick = useCallback(
    (item: OrderLineItemDto) => {
      const numericDescriptor =
        item.productDescriptor ??
        (item.shiPartNumber != null ? String(item.shiPartNumber) : null);
      const productClass =
        item.productClass || (numericDescriptor ? "shicommodity" : null);
      const orgUnitId = order.orgUnitId ?? currentOrgUnitId;
      const price =
        item.unitPrice ??
        (item.extendedPrice && item.quantity
          ? item.extendedPrice / item.quantity
          : item.extendedPrice);

      const routeId = toProductRouteId(productClass, numericDescriptor);

      pushDrawer({
        id: `product-${numericDescriptor ?? item.mfgPartNumber ?? "unknown"}`,
        component: StackedOrderProductDetailDrawer,
        props: {
          currencyCode: order.currencyCode,
          product: {
            productName: item.productName,
            mfrPartNum: item.mfgPartNumber,
            productDescriptor: numericDescriptor,
            productClass: productClass,
            price: price,
          },
          request: {
            productDescriptor: numericDescriptor,
            productClass: productClass,
            orgunitId: orgUnitId,
            cNetProductId: null,
          },
        },
        ...(routeId && {
          route: {
            segment: PRODUCT_DRAWER_SEGMENT,
            id: routeId,
            tab: PRODUCT_DRAWER_DEFAULT_TAB,
          },
        }),
      });
    },
    [pushDrawer, order.orgUnitId, order.currencyCode, currentOrgUnitId],
  );

  const handleTrackPackage = useCallback(
    (group: ShipmentGroup, status: string) => {
      const props = buildTrackingDrawerProps(order, group, status);
      if (!props) return;
      const routeId = toTrackingRouteId(order, group.trackingNumber);

      pushDrawer({
        id: `tracking-${props.trackingNumber}`,
        component: StackedTrackingDrawer,
        props,
        ...(routeId && {
          route: { segment: TRACKING_DRAWER_SEGMENT, id: routeId },
        }),
      });
    },
    [pushDrawer, order],
  );

  const hasSalesNumber = Boolean(order.salesOrder);
  const hasBiOrderId = Boolean(order.biOrderId);
  const hasOrderId = Boolean(order.biOrderId ?? order.webOrderId);
  const canWarehouseOrderRead = hasPermission(ShiOnePermission.warehouseOrderRead);

  const isLoading =
    (hasBiOrderId && isLoadingSerials) ||
    (hasBiOrderId && isLoadingRelatedData) ||
    (hasOrderId && isLoadingProductAvailability) ||
    (hasSalesNumber && canWarehouseOrderRead && isLoadingWarehouseOrders);

  if (effectiveLines.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mx: disableGutters ? 0 : 2, my: 0 }}>
      <Stack spacing={3}>
        <Stack spacing={1.2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search products by name or part number"
            value={searchTextValue}
            onChange={(e) => setSearchTextValue(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {(isLoading
              ? [{ key: "all" as const, label: "All" }]
              : availableFilters
            ).map((filter) => {
              const isActive =
                filter.key === "all"
                  ? selectedCategories.size === 0
                  : selectedCategories.has(
                      filter.key as Exclude<CategoryType, "all">,
                    );
              return (
                <Chip
                  key={filter.key}
                  label={
                    filter.key === "all" && isLoading ? (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <span>All</span>
                        <CircularProgress size={14} />
                      </Box>
                    ) : (
                      filter.label
                    )
                  }
                  size="small"
                  icon={
                    filter.key === "all" && isLoading
                      ? undefined
                      : isActive
                        ? <CheckIcon />
                        : undefined
                  }
                  color={isActive ? "primary" : "default"}
                  onClick={() => {
                    if (filter.key === "all") {
                      setSelectedCategories(new Set());
                    } else {
                      setSelectedCategories((prev) => {
                        const next = new Set(prev);
                        const key = filter.key as Exclude<CategoryType, "all">;
                        if (next.has(key)) {
                          next.delete(key);
                        } else {
                          next.add(key);
                        }
                        return next;
                      });
                    }
                  }}
                />
              );
            })}
          </Box>
        </Stack>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, value: OrderDrawerRouteTab) => setTab(value)}
            sx={{ px: 1, textTransform: "uppercase" }}
          >
            <Tab
              value="shipments"
              label={`Shipments (${shipmentCount})`}
              sx={{ alignItems: "flex-start" }}
              {...telemetryProps({
                name: "orders_detail_tab",
                section: telemetrySection,
                label: "Shipments",
                id: "shipments",
              })}
            />
            <Tab
              value="products"
              label="Products"
              sx={{ alignItems: "flex-start" }}
              {...telemetryProps({
                name: "orders_detail_tab",
                section: telemetrySection,
                label: "Products",
                id: "products",
              })}
            />
          </Tabs>
        </Box>

        {tab === "products" && (
          <ProductTable
            orderId={{
              biOrderId: order.biOrderId,
              salesOrder: order.salesOrder,
              webOrderId: order.webOrderId,
            }}
            items={filteredItems}
            serials={serials}
            relatedData={relatedData}
            myWarehouseOrders={myWarehouseOrders}
            currencyCode={order.currencyCode}
            onProductClick={handleProductClick}
            isBackorder={isBackorder}
          />
        )}

        {tab === "shipments" && (
          <OrderDrawerShipments
            orderId={{
              biOrderId: order.biOrderId,
              salesOrder: order.salesOrder,
              webOrderId: order.webOrderId,
            }}
            shipments={filteredShipmentGroups}
            onProductClick={handleProductClick}
            onTrackPackage={handleTrackPackage}
            currencyCode={order.currencyCode}
            orderDate={order.orderDate}
            shipToAddress={order.shipTo?.address}
            myWarehouseOrders={myWarehouseOrders}
            isBackorder={isBackorder}
          />
        )}
      </Stack>
    </Box>
  );
}
